import React, { useEffect, useState } from 'react';
import { api, API_BASE_URL } from '../services/api';
import '../pages/TransactionPage.css';

export default function BackOfficeFinalFixedDepositBlotter() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectingRequestId, setRejectingRequestId] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.get(`${API_BASE_URL}/fixed-deposit/requests/pending`);
      setRequests(response.data || []);
    } catch (err) {
      setError('Failed to fetch fixed deposit requests');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError('');
    setSuccess('');
    try {
      await api.put(`${API_BASE_URL}/fixed-deposit/requests/${id}/approve`, {
        approverNotes: ''
      });
      setSuccess('Request approved successfully');
      fetchRequests();
    } catch (err) {
      setError('Failed to approve request: ' + (err.response?.data?.error || err.message));
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id) => {
    if (!rejectComment.trim()) {
      setError('Comment is required when rejecting a request');
      return;
    }
    setRejectingId(id);
    setError('');
    setSuccess('');
    try {
      await api.put(`${API_BASE_URL}/fixed-deposit/requests/${id}/reject`, {
        approverNotes: rejectComment
      });
      setSuccess('Request rejected successfully');
      setRejectComment('');
      setRejectingRequestId(null);
      fetchRequests();
    } catch (err) {
      setError('Failed to reject request: ' + (err.response?.data?.error || err.message));
    } finally {
      setRejectingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toFixed(2) + '%';
  };

  return (
    <div className="transaction-blotter-container ui-page">
      <h2 className="fixed-income-header">Back Office Final Verifier - Fixed Deposit Blotter</h2>
      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : (
        <>
          {error && <div style={{ color: 'red', marginBottom: 16, padding: '10px', background: '#ffe6e6', borderRadius: '4px' }}>{error}</div>}
          {success && <div style={{ color: 'green', marginBottom: 16, padding: '10px', background: '#e6ffe6', borderRadius: '4px' }}>{success}</div>}
          {requests.length === 0 ? (
            <div>No fixed deposit requests pending authorization.</div>
          ) : (
            <div className="ui-table-wrap" style={{ overflowX: 'auto' }}>
              <table className="transactions-table ui-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Request Number</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Portfolio</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Counterparty Name</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Requested Amount</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>Target Yield</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Value Date</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Maturity Date</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'left' }}>Fund Movement</th>
                    <th style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => (
                    <tr key={request.id}>
                      <td style={{ padding: '8px', border: '1px solid #eee' }}>{request.request_no || 'N/A'}</td>
                      <td style={{ padding: '8px', border: '1px solid #eee' }}>{request.portfolio_name || request.portfolio_id || 'N/A'}</td>
                      <td style={{ padding: '8px', border: '1px solid #eee' }}>{request.counterparty_name || request.counterparty_long_name || 'N/A'}</td>
                      <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                        {formatCurrency(request.requested_amount)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'right' }}>
                        {formatPercentage(request.target_yield)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #eee' }}>{formatDate(request.value_date)}</td>
                      <td style={{ padding: '8px', border: '1px solid #eee' }}>{formatDate(request.maturity_date)}</td>
                      <td style={{ padding: '8px', border: '1px solid #eee' }}>
                        {request.fund_movement === 'yes' && request.fund_movement_type
                          ? (request.fund_movement_type === 'part_amount' ? 'Part' : 'Full')
                          : '-'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleApprove(request.id)}
                            disabled={approvingId === request.id || rejectingId === request.id}
                            style={{
                              padding: '6px 14px',
                              background: '#28a745',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: approvingId === request.id ? 'not-allowed' : 'pointer',
                              opacity: approvingId === request.id ? 0.6 : 1
                            }}
                          >
                            {approvingId === request.id ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => setRejectingRequestId(request.id)}
                            disabled={approvingId === request.id || rejectingId === request.id}
                            style={{
                              padding: '6px 14px',
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: rejectingId === request.id ? 'not-allowed' : 'pointer',
                              opacity: rejectingId === request.id ? 0.6 : 1
                            }}
                          >
                            Reject
                          </button>
                        </div>
                        {rejectingRequestId === request.id && (
                          <div style={{ marginTop: '8px', padding: '10px', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                            <textarea
                              value={rejectComment}
                              onChange={(e) => setRejectComment(e.target.value)}
                              placeholder="Enter rejection reason (required)..."
                              style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginBottom: '8px' }}
                            />
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleReject(request.id)}
                                disabled={rejectingId === request.id || !rejectComment.trim()}
                                style={{
                                  padding: '6px 14px',
                                  background: '#dc3545',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 4,
                                  cursor: rejectingId === request.id || !rejectComment.trim() ? 'not-allowed' : 'pointer',
                                  opacity: rejectingId === request.id || !rejectComment.trim() ? 0.6 : 1
                                }}
                              >
                                {rejectingId === request.id ? 'Rejecting...' : 'Confirm Reject'}
                              </button>
                              <button
                                onClick={() => {
                                  setRejectingRequestId(null);
                                  setRejectComment('');
                                }}
                                style={{
                                  padding: '6px 14px',
                                  background: '#6c757d',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 4,
                                  cursor: 'pointer'
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
